import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { getValueIf } from '../../util/getValueIf.js';
import { selectToken } from '../../util/tokenSelector.js';
import useUniqueId from '../../util/useUniqueId.js';
import { Box } from '../Box/Box.js';
import { Typography } from '../Typography/Typography.js';
import { useDirectionContext } from '../../contexts/session/Locale.js';
import { useThemeContext } from '../../contexts/session/Theme.js';
import { SliderInput } from './Slider.styled.js';
import sliderInteractiveState from './sliderInteractiveState.js';

var _excluded = ["children", "valueLabel", "accessibilityValueLabel", "getAriaLabel", "getAriaValueText", "getAriaLabeledBy", "size", "state", "values", "min", "max", "step", "readOnly", "onChange", "aria-label", "aria-labelledby"];

/**
 * Sliders allow users to make selections from a range of values.
 */
var Slider = function Slider(_ref) {
  var children = _ref.children,
    valueLabel = _ref.valueLabel,
    accessibilityValueLabel = _ref.accessibilityValueLabel,
    getAriaLabel = _ref.getAriaLabel,
    getAriaValueText = _ref.getAriaValueText,
    getAriaLabeledBy = _ref.getAriaLabeledBy,
    size = _ref.size,
    state = _ref.state,
    values = _ref.values,
    min = _ref.min,
    max = _ref.max,
    step = _ref.step,
    readOnly = _ref.readOnly,
    onChange = _ref.onChange,
    ariaLabelProps = _ref['aria-label'],
    ariaLabelledbyProps = _ref['aria-labelledby'],
    rest = _objectWithoutProperties(_ref, _excluded);
  var theme = useThemeContext();
  var direction = useDirectionContext();
  var id = useUniqueId();
  var disabled = state === 'disabled';
  var rtl = direction === 'rtl';
  var thumbSize = size === 'm' ? '28px' : '24px';
  var touchAreaHeight = '44px';
  var multipleThumbs = values.length > 1;
  var interactive = !readOnly && !disabled;
  var sliderRef = React.useRef(null);
  var secondarySliderRef = React.useRef(null);
  var sliderEventRef = React.useRef(false);
  var thumbTargetRef = React.useRef(null);
  var lastValuesRef = React.useRef(values);
  var rafId = React.useRef(null);
  var labelColor = disabled ? 'base.disabled' : 'base.default';
  var rangeColor = selectToken(theme.background, interactive ? 'product.primary' : 'deprecated.neutral.disabled-4');
  var trackColor = selectToken(theme.background, 'deprecated.neutral.5');
  var thumbStyles = {
    backgroundColor: selectToken(theme.background, 'base.surface'),
    boxShadow: selectToken(theme.shadow, 'bottom-s'),
    borderWidth: selectToken(theme.borderwidth, '2'),
    borderColor: rangeColor,
    size: thumbSize
  };
  var sliderColors = React.useMemo(function () {
    return multipleThumbs ? [trackColor, rangeColor, trackColor] : [rangeColor, trackColor];
  }, [multipleThumbs, rangeColor, trackColor]);
  var sliderBackground = React.useMemo(function () {
    var gradientDirection = rtl ? 'to left' : 'to right';
    var progress = _toConsumableArray(values).sort(function (a, b) {
      return a - b;
    }).map(function (value) {
      return (value - min) / (max - min) * 100;
    });
    var middle = progress.reduce(function (acc, point, index) {
      return "".concat(acc, ", ").concat(sliderColors[index], " ").concat(point, "%, ").concat(sliderColors[index + 1], " ").concat(point, "%");
    }, '');
    return "linear-gradient(".concat(gradientDirection, ", ").concat(sliderColors[0], " 0%").concat(middle, ", ").concat(sliderColors[sliderColors.length - 1], " 100%)");
  }, [max, min, rtl, values, sliderColors]);
  var handleMouseDown = function handleMouseDown(e) {
    thumbTargetRef.current = null;
    var from = sliderRef.current;
    var to = secondarySliderRef.current;
    if (!onChange || !from || !to) return;
    sliderEventRef.current = true;
    var track = e.target;
    var trackRect = track.getBoundingClientRect();
    var clickPosition = e.clientX - trackRect.left;
    var clickRatio = rtl ? (trackRect.width - clickPosition) / trackRect.width : clickPosition / trackRect.width;
    var getThumbDistance = function getThumbDistance(thumb) {
      return Math.abs(Number(thumb.value) / max - clickRatio);
    };
    var closestThumb = [from, to].reduce(function (prev, current) {
      return getThumbDistance(prev) < getThumbDistance(current) ? prev : current;
    });

    // This will normalize the value to the closest step
    closestThumb.value = "".concat(clickRatio * max);
    var newValues = [Number(from.value), Number(to.value)];
    lastValuesRef.current = newValues;
    onChange(newValues, false);
  };
  var handleMouseUp = function handleMouseUp() {
    // Invoke onFinalChange only if the value has changed
    if (sliderEventRef.current) {
      window.requestAnimationFrame(function () {
        return onChange === null || onChange === void 0 ? void 0 : onChange(lastValuesRef.current, true);
      });
      sliderEventRef.current = false;
    }
  };
  var handleFocus = function handleFocus(_, index) {
    thumbTargetRef.current = index;
  };
  var updateActiveThumb = React.useCallback(function (currentThumbTarget, value) {
    var currentValues = _toConsumableArray(values);
    var nextThumbValue = currentValues[currentThumbTarget + 1];
    var prevThumbValue = currentValues[currentThumbTarget - 1];
    if (nextThumbValue !== undefined && value > nextThumbValue) {
      var _secondarySliderRef$c;
      (_secondarySliderRef$c = secondarySliderRef.current) === null || _secondarySliderRef$c === void 0 || _secondarySliderRef$c.focus();
    } else if (prevThumbValue !== undefined && value < prevThumbValue) {
      var _sliderRef$current;
      (_sliderRef$current = sliderRef.current) === null || _sliderRef$current === void 0 || _sliderRef$current.focus();
    }
    currentValues[currentThumbTarget] = value;
    lastValuesRef.current = currentValues;
    return currentValues;
  }, [values]);
  var handleSliderChange = React.useCallback(function (e, index) {
    var _thumbTargetRef$curre;
    if (!onChange) return;
    sliderEventRef.current = true;
    thumbTargetRef.current = (_thumbTargetRef$curre = thumbTargetRef.current) !== null && _thumbTargetRef$curre !== void 0 ? _thumbTargetRef$curre : index;
    var value = Number(e.target.value);
    if (value === values[thumbTargetRef.current]) return; // Prevent unnecessary re-renders

    var newValues = updateActiveThumb(thumbTargetRef.current, value);
    if (rafId.current !== null) {
      window.cancelAnimationFrame(rafId.current);
    }
    rafId.current = window.requestAnimationFrame(function () {
      return onChange(newValues, false);
    });
  }, [onChange, values, thumbTargetRef, updateActiveThumb]);
  var sortedValues = _toConsumableArray(values).sort(function (a, b) {
    return a - b;
  });
  var _sortedValues = _slicedToArray(sortedValues, 2),
    first = _sortedValues[0],
    second = _sortedValues[1];
  var valueLabelString = valueLabel && (typeof valueLabel === 'string' ? valueLabel : valueLabel(first, second));
  var accessibilityValueLabelString = accessibilityValueLabel && (typeof accessibilityValueLabel === 'string' ? accessibilityValueLabel : accessibilityValueLabel(first, second));
  var renderSliderInput = function renderSliderInput(value, index) {
    var ariaLabel = getAriaLabel ? getAriaLabel(index) : getValueIf(!!ariaLabelProps, ariaLabelProps);
    var ariaLabelValueText = getAriaValueText ? getAriaValueText(value, index) : accessibilityValueLabelString;
    var ariaLabeledBy = getAriaLabeledBy ? getAriaLabeledBy(index) : getValueIf(!!ariaLabelledbyProps, ariaLabelledbyProps);
    return /*#__PURE__*/React.createElement(SliderInput
    // eslint-disable-next-line react/no-array-index-key
    , _extends({
      key: index,
      ref: index === 0 ? sliderRef : secondarySliderRef,
      type: "range",
      "data-index": index,
      value: value,
      min: min,
      max: max,
      "aria-valuetext": ariaLabelValueText || valueLabelString,
      "aria-label": ariaLabel,
      "aria-disabled": disabled,
      "aria-labelledby": ariaLabeledBy,
      disabled: !interactive,
      readOnly: readOnly,
      step: step
    }, interactive && {
      onChange: function onChange(e) {
        return handleSliderChange(e, index);
      },
      onBlur: handleMouseUp,
      onFocus: function onFocus(e) {
        return handleFocus(e, index);
      }
    }, {
      $touchAreaOffset: "-20px" // -(Touch area height - slider height / 2)
      ,
      $background: index === 0 ? sliderBackground : undefined
      // If multiple thumbs, disable pointer events because we have our own click handler
      ,
      $pointerEvents: !interactive || multipleThumbs ? 'none' : undefined,
      $thumbStyles: thumbStyles,
      $isSecondary: index === 1,
      $disabled: !interactive,
      $interactiveState: sliderInteractiveState(theme, thumbStyles.backgroundColor)
    }));
  };
  return /*#__PURE__*/React.createElement(Box, _extends({
    display: "flex",
    flexDirection: "column",
    width: "100%"
  }, rest), valueLabelString && /*#__PURE__*/React.createElement(Typography, {
    id: id,
    variant: "body-s",
    textColor: labelColor
  }, valueLabelString), children, /*#__PURE__*/React.createElement(Box, _extends({
    display: "flex",
    alignItems: "center",
    position: "relative",
    mb: "-8" // Subtract from touch area height
    ,
    height: touchAreaHeight,
    onMouseDown: multipleThumbs ? handleMouseDown : undefined
  }, interactive && {
    onMouseUp: handleMouseUp,
    onTouchEnd: handleMouseUp,
    onMouseDown: handleMouseDown,
    cursor: 'pointer'
  }), values.map(renderSliderInput)));
};
Slider.defaultProps = {
  size: 'm',
  state: 'enabled'
};
var SliderMemoized = /*#__PURE__*/React.memo(Slider);
SliderMemoized.displayName = 'Slider';

export { Slider as BaseSlider, SliderMemoized as Slider };
//# sourceMappingURL=Slider.js.map
